import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AssetDto, GalleryDto } from '@picflow/types';
import { Subject, takeUntil } from 'rxjs';
import { ScreenSizes } from '../../../classes/screen-sizes.class';
import { GalleryAccessLevel } from '../../../lib/access-level';
import { getFormControl } from '../../../lib/get-form-control';
import { sleep } from '../../../lib/sleep';
import { GalleryService } from '../../../services/gallery.service';
import { ShareService } from '../../../services/share.service';
import { StateService } from '../../../services/state.service';
import { ToastService } from '../../../services/toast.service';
import { InputFieldComponent } from '../../../shared-extra/components/input-field/input-field.component';
import { InputPostDirective } from '../../../shared-extra/directives/input-post.directive';
import { ShareDirective } from '../../../shared-extra/directives/share.directive';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { AutofocusDirective } from '../../../shared/directives/autofocus.directive';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';

@Component({
  selector: 'app-editable-gallery-url-field',
  templateUrl: './editable-gallery-url-field.component.html',
  styleUrls: ['./editable-gallery-url-field.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InputFieldComponent,
    TooltipDirective,
    InputPostDirective,
    ButtonDirective,
    ShareDirective,
    FormsModule,
    ReactiveFormsModule,
    LoadingIndicatorComponent,
    AutofocusDirective,
  ],
})
export class EditableGalleryUrlFieldComponent implements OnInit, OnDestroy {
  public state = inject(StateService);
  private shareService = inject(ShareService);
  private galleryService = inject(GalleryService);
  private toasts = inject(ToastService);
  private check = inject(ChangeDetectorRef);
  @Input() gallery: GalleryDto;
  @Input() asset: AssetDto;
  @Input() accessLevel: GalleryAccessLevel;
  @Output() onEditingPathChange = new EventEmitter<boolean>();
  canShare = navigator.share && ScreenSizes.screenSize() !== 'desktop';
  pathForm = new FormGroup({
    path: getFormControl('galleryPath'),
  });
  publicLink: string;
  publicLinkPre: string;
  publicLinkPost: string;
  editingPath = false;
  savingPath = false;

  destroyed$ = new Subject<void>();

  ngOnInit(): void {
    this.pathForm.controls.path.setValue(this.gallery.path);
    this.getLink();
    this.galleryService.onGalleryUpdated$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.check.detectChanges();
    });
  }

  private async getLink() {
    const link = await this.shareService.getShareLink(this.gallery, { assetId: this.asset?.id });
    this.publicLink = link.url;
    this.publicLinkPre = link.pre;
    this.publicLinkPost = link.post;
    this.check.detectChanges();
  }

  async startEditPath() {
    if (
      this.gallery.public_access === 'no_access' ||
      (this.gallery.tenant || this.state.activeTenant)['block_public_link_access']
    )
      return;
    if (this.accessLevel !== 'edit') return;
    await sleep(0); // needed otherwise context closes
    this.editingPath = true;
    this.onEditingPathChange.emit(true);
  }

  async cancelEditPath() {
    await sleep(0); // needed otherwise context closes
    this.editingPath = false;
    this.onEditingPathChange.emit(false);
  }

  async savePath() {
    await sleep(0);
    if (!this.pathForm.valid) return;
    if (this.savingPath) return;
    const path = this.pathForm.value.path;
    if (!path || path === this.gallery.path) {
      await sleep(0); // needed otherwise context closes
      this.editingPath = false;
      this.onEditingPathChange.emit(false);
      return;
    }
    this.savingPath = true;
    try {
      await this.galleryService.updateGallery(this.gallery.id, { path });
      this.editingPath = false;
      this.onEditingPathChange.emit(false);
      this.gallery.path = path;
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (err.error.code !== 'gallery_path_exists') throw err;
      this.toasts.add('This link already exists!', 'error');
    }
    this.savingPath = false;
    this.getLink();
  }

  onCopied() {
    this.toasts.add('Link Copied', 'success');
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
